/**
 * Shared code of the Iconimate icons (https://iconimate.app, MIT — see LICENSE.md).
 * Every Iconimate file repeats these helpers; here they live once and the icon
 * files keep only their glyph, variants and component.
 *
 * Changed from the originals: one pass per start instead of a replay loop while
 * hovered — the icons sit in a touch menu, where a tap is the gesture and a
 * mobile browser's emulated hover never ends.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { DOMAttributes, HTMLAttributes } from 'react';
import { useAnimation, useReducedMotion } from 'motion/react';
import type { Transition } from 'motion/react';

/** Imperative handle every icon exposes — lets consumers trigger motion on touch, where `:hover` never fires. */
export interface AnimatedIconHandle {
  startAnimation(): void;
  stopAnimation(): void;
}

export interface AnimatedIconProps extends HTMLAttributes<HTMLDivElement> {
  /** Rendered width & height in px. Defaults to 24 (the set's ship size). */
  size?: number;
}

/** A cubic-bezier easing curve. */
export type Bezier = [number, number, number, number];

/** Gentle standard glide — used by every "normal" variant for hover-out. */
export const RETURN: Bezier = [0.4, 0, 0.2, 1];

/** Duration scale in seconds, calibrated for legibility at the 24px ship size. */
export const DUR = { instant: 0.12, fast: 0.2, base: 0.32, slow: 0.5 } as const;

/**
 * The canonical hover-out transition. Spread into every "normal" variant so that
 * interrupting a hover glides the icon home instead of snapping.
 */
export const RETURN_TRANSITION: Transition = { duration: DUR.base, ease: RETURN };

/** Exaggeration — an ease that overshoots its target then eases back. */
export const OVERSHOOT_BACK: Bezier = [0.34, 1.56, 0.64, 1];

/** Staging / Overlapping action — the per-element delay for a staggered cascade. */
export function staged(index: number, step = 0.09): number {
  return index * step;
}

/** The controls object returned by `useAnimation()` — derived to stay resilient to motion's type renames. */
type AnimationControls = ReturnType<typeof useAnimation>;

export interface HoverController {
  /** The single controls instance every animated element in the icon is gated through. */
  controls: AnimationControls;
  /**
   * True when the icon should render its static fallback instead of animating.
   * Not the OS preference: a gesture the user asked for still plays once.
   */
  reduced: boolean;
  /** False when the user prefers reduced motion: nothing may repeat on its own. */
  ambient: boolean;
  /** Play the "animate" variant once. */
  start: () => void;
  /** Glide back to the "normal" variant. */
  stop: () => void;
  /** Spread onto the icon's wrapper. Keyboard focus triggers it too, not just pointer. */
  bind: Pick<DOMAttributes<Element>, 'onMouseEnter' | 'onMouseLeave' | 'onFocus' | 'onBlur'>;
}

/** Owns one `useAnimation` instance and the enter / leave / focus / blur wiring. */
export function useHover(): HoverController {
  const controls = useAnimation();
  const reduced = false;
  const ambient = !(useReducedMotion() ?? false);
  const playing = useRef(false);
  const mounted = useRef(true);

  const start = useCallback(() => {
    if (playing.current) return; // a pass is running — don't stack another
    playing.current = true;
    void controls.start('animate').then(() => {
      playing.current = false;
      // Keyframes end where they start, so this snap is invisible; it lets the
      // next start("animate") replay instead of resolving at once.
      if (mounted.current) controls.set('normal');
    });
  }, [controls]);

  const stop = useCallback(() => {
    playing.current = false;
    void controls.start('normal');
  }, [controls]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return {
    controls,
    reduced,
    ambient,
    start,
    stop,
    bind: { onMouseEnter: start, onFocus: start },
  };
}
